import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Draggable from "react-draggable";
import { useCountry } from "../context/CountryContext";

// ===== YOUR NOMBA PAYMENT LINK =====
const NOMBA_PAYMENT_LINK = "https://flutterwave.com/pay/prorealalertsubscription";

const FloatingNotice = ({ message, onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: -20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    className="fixed top-6 left-1/2 transform -translate-x-1/2 bg-red-600/90 text-white px-6 py-3 rounded-xl shadow-xl z-50"
  >
    <div className="flex items-center justify-between gap-4">
      <span>{message}</span>
      <button onClick={onClose} className="font-bold text-white">×</button>
    </div>
  </motion.div>
);

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const { isGhana, config } = useCountry();

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [instructionsCountdown, setInstructionsCountdown] = useState(5);
  const [exchangeRate, setExchangeRate] = useState(isGhana ? 14 : 1600);
  const [notice, setNotice] = useState("");

  const userPlan = JSON.parse(localStorage.getItem("user"))?.plan || null;

  const planPricesUSD = {
    Basic: 25.0,
    Premium: 35.0,
    Elite: 55.0,
  };

  const theme = isGhana
    ? {
        bgGlow: "from-yellow-600 via-red-600 to-orange-500",
        titleGradient: "from-yellow-400 to-red-500",
        title: "Upgrade to ProBank Ghana Premium",
        currencySymbol: "₵",
        currencyCode: "GHS",
        rateApiCode: "GHS",
        flag: "🇬🇭",
        basicColor: "text-yellow-400",
        premiumColor: "text-green-400",
        eliteColor: "text-red-400",
        basicBtn: "from-yellow-400 to-yellow-600",
        premiumBtn: "from-green-400 to-emerald-600",
        eliteBtn: "from-red-500 to-orange-600",
        confirmBtn: "bg-yellow-400 text-black",
        floatingBtn: "bg-yellow-400 text-black",
        modalAccent: "text-yellow-300",
        modalAmount: "text-red-400",
        primaryGradient: "from-yellow-400 to-red-500",
      }
    : {
        bgGlow: "from-purple-700 via-pink-600 to-blue-500",
        titleGradient: "from-cyan-400 to-violet-500",
        title: "Upgrade to Probank 15v Premium",
        currencySymbol: "₦",
        currencyCode: "NGN",
        rateApiCode: "NGN",
        flag: "🇳🇬",
        basicColor: "text-sky-400",
        premiumColor: "text-green-400",
        eliteColor: "text-amber-400",
        basicBtn: "from-sky-500 to-blue-600",
        premiumBtn: "from-green-400 to-emerald-600",
        eliteBtn: "from-amber-400 to-yellow-500",
        confirmBtn: "bg-green-400 text-black",
        floatingBtn: "bg-green-400 text-black",
        modalAccent: "text-sky-300",
        modalAmount: "text-violet-400",
        primaryGradient: "from-cyan-400 to-violet-500",
      };

  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouse = (e) => setMouse({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", handleMouse);
    return () => window.removeEventListener("mousemove", handleMouse);
  }, []);

  useEffect(() => {
    const fetchRate = async () => {
      try {
        const res = await fetch("https://api.exchangerate-api.com/v4/latest/USD");
        const data = await res.json();
        if (data?.rates?.[theme.rateApiCode]) {
          setExchangeRate(data.rates[theme.rateApiCode]);
        }
      } catch (err) {
        console.error("Rate fetch failed", err);
      }
    };
    fetchRate();
  }, [theme.rateApiCode]);

  // Countdown for instructions
  useEffect(() => {
    if (!showInstructions) return;
    setInstructionsCountdown(5);
    const timer = setInterval(() => {
      setInstructionsCountdown((p) => {
        if (p <= 1) {
          clearInterval(timer);
          return 0;
        }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [showInstructions]);

  const getLocalAmount = (plan) => {
    return Math.round(planPricesUSD[plan] * exchangeRate);
  };

  const getLocalAmountDisplay = (plan) => {
    return getLocalAmount(plan).toLocaleString();
  };

  const handleSubscribe = (plan) => {
    const assigned = userPlan ? userPlan.toLowerCase() : null;
    const clicked = plan.toLowerCase();

    if (assigned && clicked !== assigned) {
      setNotice(
        `Your account is assigned to the ${userPlan} plan. Kindly select ${userPlan} to subscribe.`
      );
      setTimeout(() => setNotice(""), 4000);
      return;
    }

    setSelectedPlan(plan);
    setIsConfirmOpen(true);
  };

  const confirmPayment = async () => {
    try {
      if (!selectedPlan) return;

      const userId = localStorage.getItem("userId");

      // The plan choice is stored locally and submitted with the
      // payment itself. There is no standalone backend call here.

      // Update localStorage so the success page can show plan
      const user = JSON.parse(localStorage.getItem("user")) || {};
      user.pendingPlan = selectedPlan.toLowerCase();
      localStorage.setItem("user", JSON.stringify(user));

      // Close confirm modal, open instructions modal
      setIsConfirmOpen(false);
      setShowInstructions(true);
    } catch (err) {
      console.error("Subscription failed:", err);
      setNotice("Failed to save your plan. Try again.");
    }
  };

  const copyAmount = () => {
    if (!selectedPlan) return;
    const amount = getLocalAmount(selectedPlan);
    navigator.clipboard.writeText(String(amount));
    setNotice(`✅ ${theme.currencySymbol}${amount.toLocaleString()} copied!`);
    setTimeout(() => setNotice(""), 2500);
  };

  const goToPayment = () => {
    window.location.href = NOMBA_PAYMENT_LINK;
  };

  const calculateTilt = () => {
    const xOffset = (mouse.x - window.innerWidth / 2) / 40;
    const yOffset = (mouse.y - window.innerHeight / 2) / 40;
    return { rotateX: -yOffset, rotateY: xOffset };
  };

  const planDescriptions = {
    Basic: "Access standard features and alerts.",
    Premium: "Unlock advanced tools and full dashboard access.",
    Elite: "Full access, real-time alerts and priority support.",
  };

  const planTaglines = {
    Basic: "Alert reflection",
    Premium: "Pay off loans",
    Elite: "Inter. transactions",
  };

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      <div className="fixed top-4 right-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900/60 border border-zinc-700/50 backdrop-blur-md">
        <span className="text-base leading-none">{theme.flag}</span>
        <span className="text-xs text-slate-300 font-medium">{config.name}</span>
      </div>

      <div className="absolute inset-0 z-0">
        <motion.div
          className={`w-[200%] h-[200%] bg-gradient-to-r ${theme.bgGlow} opacity-20 blur-3xl`}
          animate={{ x: ["0%", "-50%"], y: ["0%", "-50%"] }}
          transition={{ repeat: Infinity, duration: 40, ease: "linear" }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 py-16 text-white">
        <motion.h1
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className={`text-5xl md:text-6xl font-extrabold mb-8 text-center bg-gradient-to-r ${theme.titleGradient} bg-clip-text text-transparent neon-text`}
        >
          {theme.title}
        </motion.h1>

        <motion.p
          className="text-center text-gray-400 mb-12 text-lg md:text-xl max-w-2xl mx-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          Unlock advanced features and experience seamless real-time banking.
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {["Basic", "Premium", "Elite"].map((plan) => {
            const tilt = calculateTilt();
            const planColorMap = {
              Basic: theme.basicColor,
              Premium: theme.premiumColor,
              Elite: theme.eliteColor,
            };
            const planBtnMap = {
              Basic: theme.basicBtn,
              Premium: theme.premiumBtn,
              Elite: theme.eliteBtn,
            };

            return (
              <motion.div
                key={plan}
                whileHover={{ scale: 1.05 }}
                style={{ perspective: 1000, ...tilt }}
                className="relative bg-zinc-900/50 backdrop-blur-xl border border-zinc-800 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-transform"
              >
                <h3 className={`text-2xl font-bold mb-4 ${planColorMap[plan]}`}>
                  {plan}
                </h3>

                <p className="text-gray-300 mb-6 min-h-[60px]">
                  {planDescriptions[plan]}
                </p>

                <p className={`text-3xl font-extrabold mb-2 bg-gradient-to-r ${theme.titleGradient} bg-clip-text text-transparent`}>
                  ${planPricesUSD[plan]} / {planTaglines[plan]}
                </p>

                <p className="text-sm text-gray-400 mb-8">
                  ≈ {theme.currencySymbol}{getLocalAmountDisplay(plan)} ({theme.currencyCode})
                </p>

                <button
                  onClick={() => handleSubscribe(plan)}
                  className={`w-full py-3 rounded-xl font-semibold transition bg-gradient-to-r ${planBtnMap[plan]} text-black`}
                >
                  Subscribe
                </button>
              </motion.div>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <button
            onClick={() => navigate("/dashboard")}
            className="px-8 py-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white font-semibold border border-zinc-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>

      <Draggable>
        <motion.button
          onClick={() => navigate("/subscription")}
          className={`fixed bottom-10 right-10 px-6 py-4 rounded-full ${theme.floatingBtn} font-bold shadow-xl neon-glow cursor-move z-50`}
          animate={{ y: [0, -10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          Subscribe
        </motion.button>
      </Draggable>

      {/* CONFIRM MODAL */}
      <AnimatePresence>
        {isConfirmOpen && selectedPlan && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-sm w-full text-center"
            >
              <h2 className="text-2xl font-bold mb-4">Confirm Subscription</h2>

              <p className="text-gray-400 mb-4">
                You are about to subscribe to
                <span className={`${theme.modalAccent} font-semibold`}> {selectedPlan}</span>
              </p>

              <p className={`mb-2 ${theme.modalAmount} font-bold text-lg`}>
                ${planPricesUSD[selectedPlan]} ≈ {theme.currencySymbol}
                {getLocalAmountDisplay(selectedPlan)}
              </p>

              <p className="text-xs text-gray-500 mb-6">
                Pricing for {theme.flag} {config.name}
              </p>

              <div className="flex gap-4 justify-center flex-wrap">
                <button
                  onClick={confirmPayment}
                  className={`${theme.confirmBtn} px-6 py-3 rounded-xl font-semibold`}
                >
                  Proceed to Pay
                </button>

                <button
                  onClick={() => setIsConfirmOpen(false)}
                  className="bg-zinc-700 px-6 py-3 rounded-xl text-white"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* INSTRUCTIONS MODAL (NEW - Nomba TAP TO COPY) */}
      <AnimatePresence>
        {showInstructions && selectedPlan && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 backdrop-blur-sm p-4 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-zinc-900 border border-zinc-800 rounded-3xl p-6 max-w-md w-full space-y-5 my-4"
            >
              <div className="text-center">
                <div className="text-4xl mb-1">📋</div>
                <h2 className="text-xl font-bold text-white">Follow These Steps</h2>
                <p className="text-gray-400 text-xs mt-1">Takes 30 seconds</p>
              </div>

              {/* COPY AMOUNT */}
              <div className="p-4 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/10 border-2 border-green-400/40 space-y-3">
                <p className="text-center text-green-300 font-semibold text-sm">
                  Step 1: Copy the amount
                </p>
                <button
                  onClick={copyAmount}
                  className="w-full p-3 rounded-xl bg-black/40 border border-green-400/30 hover:bg-black/60 transition flex items-center justify-between"
                >
                  <span className="text-2xl font-bold text-green-400">
                    {theme.currencySymbol}{getLocalAmountDisplay(selectedPlan)}
                  </span>
                  <span className="text-xs bg-green-500 text-black font-bold px-3 py-2 rounded-lg">
                    📋 TAP TO COPY
                  </span>
                </button>
                <p className="text-center text-xs text-green-200/80">
                  For {selectedPlan} plan
                </p>
              </div>

              {/* STEPS */}
              <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-400/30 space-y-2">
                <p className="text-blue-300 font-semibold text-sm">📲 On the payment page:</p>
                <ol className="text-blue-100 text-sm space-y-2 pl-1">
                  <li>
                    <b className="text-blue-400">2.</b> Tap the{" "}
                    <span className="font-bold text-white bg-white/10 px-2 py-0.5 rounded">Amount</span>{" "}
                    field and <b className="text-white">paste</b>{" "}
                    <b className="text-green-300">
                      {getLocalAmountDisplay(selectedPlan)}
                    </b>
                  </li>
                  <li>
                    <b className="text-blue-400">3.</b> Choose your payment option (Card, Transfer, USSD)
                  </li>
                  <li>
                    <b className="text-blue-400">4.</b> Complete the payment
                  </li>
                  <li>
                    <b className="text-blue-400">5.</b> You'll be brought back to send proof
                  </li>
                </ol>
              </div>

              <div className="p-3 rounded-xl bg-yellow-500/15 border border-yellow-400/40 text-yellow-200 text-xs text-center">
                💡 Paste the exact amount: <b className="text-white">{theme.currencySymbol}{getLocalAmountDisplay(selectedPlan)}</b>
              </div>

              <button
                onClick={goToPayment}
                disabled={instructionsCountdown > 0}
                className={`w-full py-4 rounded-xl font-bold text-base shadow-lg ${
                  instructionsCountdown > 0
                    ? "bg-gray-600 cursor-not-allowed text-gray-300"
                    : `bg-gradient-to-r ${theme.primaryGradient} hover:brightness-110 text-white active:scale-[0.98]`
                }`}
              >
                {instructionsCountdown > 0
                  ? `Please read first... (${instructionsCountdown}s)`
                  : "✅ Got It — Take Me to Payment"}
              </button>

              <button
                onClick={() => setShowInstructions(false)}
                className="w-full py-1 text-xs text-gray-400 hover:text-white"
              >
                ← Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {notice && (
          <FloatingNotice
            message={notice}
            onClose={() => setNotice("")}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default SubscriptionPage;
